import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useState, useEffect, useCallback} from 'react';

import {assert} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AutomationOutputFileConfig} from 'lab-automation/automation-files/LabAutomationConstants';
import {
  AutomationOutputFileTransformDataType,
  ClientAutomationOutputFileConfigSpec,
} from 'lab-automation/settings/RunSchemaSpecConstants';
import FiltersContainer from 'search/filters/FiltersContainer';
import {Filter, FilterKey, Operator} from 'search/SearchConstants';
import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import HorizontalThrobber, {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import Link from 'ui-library/Link';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import * as DateUtil from 'util/DateUtil';
import {InputChangeEvent} from 'util/Types';

import {ConnectionNameQuery, ConnectionNameQueryVariables} from './__generated/ConnectionNameQuery';
import {
  FILE_STATUS_TO_TEXT,
  InstrumentData,
  InstrumentDataFileType,
  InstrumentFileStatus,
  isUnselectableFileStatus,
} from './InstrumentDataConstants';
import {searchInstrumentData} from './InstrumentDataDAO';
import RefetchFileButton from './RefetchFileButton';

export const CONNECTION_NAME_QUERY: DocumentNode = gql`
  query ConnectionNameQuery($connectionId: StrictString!) @skipAudit {
    connection(id: $connectionId) {
      ... on ConnectionNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function ConnectionName(props: {connectionId: string}): JSX.Element {
  const result = useBenchlingQuery<ConnectionNameQuery, ConnectionNameQueryVariables>(CONNECTION_NAME_QUERY, {
    variables: {connectionId: props.connectionId},
  });

  if (result.type !== QueryResultType.SUCCESS) {
    return <HorizontalThrobber />;
  }
  if (isInaccessible(result.data.connection)) {
    return <></>;
  }
  return <>{result.data.connection.name}</>;
}

export const USER_NAME_QUERY: DocumentNode = gql`
  query UserNameQuery($entityId: StrictString!) @skipAudit {
    user(id: $entityId) {
      ... on UserNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const styles = StyleSheet.create({
  tableContainer: {
    maxHeight: '400px',
    overflowY: 'auto',
  },
  table: {
    borderLeft: 0,
    borderRight: 0,
    marginBottom: 0,
    marginRight: 0,
    marginLeft: 0,
  },
  tableRow: {
    height: '36px',
  },
  tableCell: {
    padding: '0 13px',
  },
  checkboxCell: {
    textAlign: 'center',
    padding: 0,
    width: '20px',
    marginTop: '16px',
  },
  checkbox: {
    accentColor: Colors.BLUE,
  },
  searchFilter: {
    width: '350px',
    marginRight: '10px',
  },
  statusIcon: {
    marginRight: '6px',
  },
  errored: {
    color: Colors.RED,
  },
  not_uploaded: {
    color: Colors.GRAY_DARK,
  },
  uploaded: {
    color: Colors.GREEN,
  },
  format_error: {
    color: Colors.ORANGE,
  },
});
function InstrumentDataTableCell({
  className = css(styles.tableCell),
  children,
  tooltipText,
  onSelectRow,
}: {
  className?: string;
  children: JSX.Element;
  tooltipText?: JSX.Element | undefined;
  onSelectRow: () => void;
}): JSX.Element {
  return (
    <Cell className={className} onClick={onSelectRow}>
      <Tooltip isDisabled={tooltipText == null} text={tooltipText}>
        {children}
      </Tooltip>
    </Cell>
  );
}

const FILE_STATUS_TO_STYLE: {[status in InstrumentFileStatus]: keyof typeof styles} = {
  [InstrumentFileStatus.ERRORED]: 'errored',
  [InstrumentFileStatus.NOT_UPLOADED]: 'not_uploaded',
  [InstrumentFileStatus.UPLOADED]: 'uploaded',
  [InstrumentFileStatus.FORMAT_ERROR]: 'format_error',
};

const FILE_STATUS_TO_ICON: {[status in InstrumentFileStatus]: BenchlingIconName} = {
  [InstrumentFileStatus.ERRORED]: 'request-failed',
  [InstrumentFileStatus.NOT_UPLOADED]: 'request-in-progress',
  [InstrumentFileStatus.UPLOADED]: 'request-executed',
  [InstrumentFileStatus.FORMAT_ERROR]: 'warning',
};

export function fileStatusDisplay(status: InstrumentFileStatus): JSX.Element {
  return (
    <>
      <BenchlingIcon
        className={css(styles.statusIcon, styles[FILE_STATUS_TO_STYLE[status]])}
        icon={FILE_STATUS_TO_ICON[status]}
      />
      {FILE_STATUS_TO_TEXT[status]}
    </>
  );
}

const COLUMN_HEADER_TO_CELL: {
  [columnHeader: string]: (
    instrumentData: InstrumentData,
    props: InstrumentDataTableProps,
    onRefetchComplete: () => Promise<void>
  ) => JSX.Element | string | undefined;
} = {
  Connection: (instrumentData: InstrumentData) => (
    <ConnectionName connectionId={instrumentData.connectionId} />
  ),

  'File name': (instrumentData: InstrumentData, props: InstrumentDataTableProps) => {
    // TODO remove instrumentData.filename when no longer needed, kept for backwards compatibility
    const filename = instrumentData.displayFilename || instrumentData.filename;
    if (props.onSelectRow && props.hideCheckboxColumn) {
      return <a>{filename}</a>;
    }
    return filename;
  },
  'Data type': (instrumentData: InstrumentData) => instrumentData.dataType,
  'Modified at': (instrumentData: InstrumentData) => DateUtil.formalTime(instrumentData.modifiedAt),
  'Created at': (instrumentData: InstrumentData) => DateUtil.formalTime(instrumentData.createdAt),
  Refetch: (
    instrumentData: InstrumentData,
    props: InstrumentDataTableProps,
    onRefetchComplete: () => Promise<void>
  ) => (
    <RefetchFileButton
      instrumentData={instrumentData}
      isConnectionAvailable={props.isConnectionAvailable ?? false}
      loginUsername={props.loginUsername ?? null}
      automationOutputFileConfig={props.automationOutputFileConfig ?? null}
      onRefetchComplete={onRefetchComplete}
    />
  ),
  Status: (instrumentData: InstrumentData) => fileStatusDisplay(instrumentData.status),
};

interface InstrumentDataTableProps {
  instrumentData: Array<InstrumentData>;
  setInstrumentData: (instrumentData: Array<InstrumentData>) => void;
  selectedRowIndices?: Set<number>;
  onSelectRow?: (rowIndex: number) => void;
  allowMultiple?: boolean;
  columns: Array<keyof typeof COLUMN_HEADER_TO_CELL>;
  filterOptions: Array<FilterKey.CONNECTION>;
  hideCheckboxColumn?: boolean;
  instrumentQueryFilter?: Array<string>;
  transformDataTypeFilter?: AutomationOutputFileTransformDataType | null;
  initialFilterValues?: {
    [FilterKey.CONNECTION]?: Array<string>;
  };
  isImporting?: boolean;
  isConnectionAvailable?: boolean;
  children?: React.ReactNode;
  loginUsername?: string | null;
  automationOutputFileConfig?: AutomationOutputFileConfig | ClientAutomationOutputFileConfigSpec | null;
}

export default function InstrumentDataTable(props: InstrumentDataTableProps): JSX.Element {
  const {instrumentQueryFilter, transformDataTypeFilter, selectedRowIndices, onSelectRow, setInstrumentData} =
    props;
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [connectionFilter, setConnectionFilter] = useState(
    props.initialFilterValues?.[FilterKey.CONNECTION] ?? []
  );
  const [nextToken, setNextToken] = useState('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const canFetchMore = nextToken !== '';

  const fetchData = useCallback(
    async (nextToken: string) =>
      await searchInstrumentData({
        searchFilter,
        connectionFilter,
        nextToken,
        fileTypeFilter: InstrumentDataFileType.ORIGINAL,
        instrumentQueryFilter: instrumentQueryFilter ?? [],
        transformDataTypeFilter: transformDataTypeFilter ?? null,
      }),
    [searchFilter, connectionFilter, instrumentQueryFilter, transformDataTypeFilter]
  );
  const fetchInitialData = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    const results = await fetchData('');
    setInstrumentData(results.instrumentData);
    setNextToken(results.nextToken);
    setIsLoading(false);
  }, [fetchData, setInstrumentData]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  function getFilterForKey(filterKey: FilterKey): Filter {
    switch (filterKey) {
      case FilterKey.CONNECTION:
        return {key: FilterKey.CONNECTION, value: connectionFilter ?? [], operator: Operator.IS_ONE_OF};
      default:
        assert(false, `Unexpected filter key in InstrumentDataFilters: ${filterKey}`);
    }
  }

  return (
    <div>
      <div className="u-flex-display u-spacing-bottom">
        <PlainSearchInput
          className={css(styles.searchFilter)}
          clearType={ClearType.X}
          onEscape={() => {
            setSearchFilter('');
          }}
          onChange={(e: InputChangeEvent) => {
            setSearchFilter(e.target.value);
          }}
          placeholder="Search"
          value={searchFilter}
        />
        <FiltersContainer
          primaryFilterOptions={props.filterOptions.map((filterKey) => ({filterKey}))}
          filters={props.filterOptions.map((filterKey) => getFilterForKey(filterKey))}
          onChangeFilters={(filters) => {
            for (const filter of filters) {
              switch (filter.key) {
                case FilterKey.CONNECTION:
                  setConnectionFilter(filter.value);
                  break;
                default:
                  assert(false, `Unexpected filter key in ImportInstrumentDataModal: ${filter.key}`);
              }
            }
          }}
          selectedSchema={null}
        />
      </div>
      {props.isImporting || isLoading ? (
        <CenteredHorizontalThrobber />
      ) : (
        <>
          <div className={css(styles.tableContainer)}>
            <Table
              emptyTableText="No instrument data"
              numRows={props.instrumentData.length}
              overflowVisible={true}
              className={css(styles.table)}
              getRowClassName={() => css(styles.tableRow)}
            >
              {selectedRowIndices != null && onSelectRow != null && !props.hideCheckboxColumn ? (
                <Column
                  header={
                    props.allowMultiple ? (
                      <Cell className={css(styles.checkboxCell)}>
                        <IndeterminateCheckbox
                          items={[...Array(props.instrumentData.length).keys()]}
                          selectedMap={(idx: number): boolean => selectedRowIndices.has(idx)}
                          disabled={!props.allowMultiple}
                          onChange={() => {
                            [...Array(props.instrumentData.length).keys()].forEach((i) => {
                              if (selectedRowIndices.size === 0 || selectedRowIndices.has(i)) {
                                onSelectRow(i);
                              }
                            });
                          }}
                        />
                      </Cell>
                    ) : (
                      <Cell className={css(styles.checkboxCell)} />
                    )
                  }
                  renderCell={(rowIndex: number) => (
                    <InstrumentDataTableCell
                      className={css(styles.checkboxCell)}
                      onSelectRow={() => {
                        onSelectRow(rowIndex);
                      }}
                    >
                      <input
                        type="checkbox"
                        className={css(styles.checkbox)}
                        disabled={
                          isUnselectableFileStatus(
                            props.instrumentData[rowIndex].status,
                            props.isConnectionAvailable
                          ) ||
                          (!props.allowMultiple &&
                            selectedRowIndices.size > 0 &&
                            !selectedRowIndices.has(rowIndex))
                        }
                        checked={selectedRowIndices.has(rowIndex)}
                        onChange={
                          noop /* Required to avoid React warning, even though we are controlling the change at the cell level */
                        }
                      />
                    </InstrumentDataTableCell>
                  )}
                />
              ) : null}
              {props.columns.map((header, i) => (
                <Column
                  key={i}
                  header={header}
                  renderCell={(rowIndex: number) => {
                    const statusInfo = props.instrumentData[rowIndex].statusInfo;
                    return (
                      <InstrumentDataTableCell
                        onSelectRow={
                          onSelectRow && header !== 'Refetch' // clicking the Refetch button shouldn't select the row
                            ? () => {
                                onSelectRow(rowIndex);
                              }
                            : noop
                        }
                        tooltipText={
                          header === 'Status' && statusInfo ? (
                            <>
                              {statusInfo}
                              {statusInfo?.slice(-1) !== '.' ? '.' : ''}
                              <br />
                              {'Please visit our '}
                              <Link
                                href="https://help.benchling.com/hc/en-us/sections/21197031688717-Benchling-Connect-Adapter-specific-guides"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                adapter configuration guides
                              </Link>
                              {' or contact Benchling Support for additional help.'}
                            </>
                          ) : undefined
                        }
                      >
                        <>
                          {COLUMN_HEADER_TO_CELL[header](
                            props.instrumentData[rowIndex],
                            props,
                            fetchInitialData
                          )}
                        </>
                      </InstrumentDataTableCell>
                    );
                  }}
                />
              ))}
            </Table>
            {canFetchMore && (
              <div className="u-text-align-center u-small-spacing">
                {isLoadingMore ? (
                  <span className="u-faded">Loading...</span>
                ) : (
                  <a
                    onClick={async () => {
                      setIsLoadingMore(true);
                      const results = await fetchData(nextToken);
                      setInstrumentData([...props.instrumentData, ...results.instrumentData]);
                      setNextToken(results.nextToken);
                      setIsLoadingMore(false);
                    }}
                  >
                    Load more instrument data
                  </a>
                )}
              </div>
            )}
          </div>
          {props.children}
        </>
      )}
    </div>
  );
}
